// Camada HTTP do dominio transactions/expenses/incomes. Wrappers finos sobre
// `api` (ky) — sem React, sem store. Mutations/queries TanStack vivem em
// `transactions.queries.ts`.
//
// Backend doc canonico:
//   - GET  /transactions[/summary]     -> docs/api/transactions.md
//   - CRUD /expenses                   -> docs/api/expenses.md
//   - CRUD /incomes                    -> docs/api/incomes.md

import type {
  CreateExpensePayload,
  CreateIncomePayload,
  Expense,
  Income,
  TransactionItem,
  TransactionSummary,
  UpdateExpensePayload,
  UpdateIncomePayload,
} from '../types/transactions.types';

import { api } from '@/shared/api/client';
import type { ApiResponse, PaginatedResponse } from '@/shared/api/client.types';
import { idempotencyHeaders } from '@/shared/lib/idempotency';

export interface TransactionsListParams {
  page?: number;
  per_page?: number;
  start_date?: string;
  end_date?: string;
  category_id?: string;
  family_group_id?: string;
  type?: 'expense' | 'income';
  search?: string;
  sort_order?: 'asc' | 'desc';
}

export interface TransactionsSummaryParams {
  start_date?: string;
  end_date?: string;
  family_group_id?: string;
}

function cleanParams(params: object): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = String(v);
  }
  return out;
}

export const transactionsApi = {
  list: (params: TransactionsListParams = {}) =>
    api
      .get('transactions', { searchParams: cleanParams(params) })
      .json<PaginatedResponse<TransactionItem>>(),

  summary: (params: TransactionsSummaryParams = {}) =>
    api
      .get('transactions/summary', { searchParams: cleanParams(params) })
      .json<ApiResponse<TransactionSummary>>(),
};

export interface ExpensesListParams extends TransactionsListParams {
  payment_method?: string;
  is_recurring?: boolean;
}

export const expensesApi = {
  list: (params: ExpensesListParams = {}) =>
    api.get('expenses', { searchParams: cleanParams(params) }).json<PaginatedResponse<Expense>>(),

  get: (id: string) => api.get(`expenses/${id}`).json<ApiResponse<Expense>>(),

  create: (body: CreateExpensePayload, idempotencyKey?: string) =>
    api
      .post('expenses', { json: body, headers: idempotencyHeaders(idempotencyKey) })
      .json<ApiResponse<Expense | Expense[]>>(),

  update: (id: string, body: UpdateExpensePayload, idempotencyKey?: string) =>
    api
      .patch(`expenses/${id}`, { json: body, headers: idempotencyHeaders(idempotencyKey) })
      .json<ApiResponse<Expense>>(),

  // DELETE retorna 204 No Content. Usamos `.then()` para coercir em void
  // (ky.json() em 204 quebra; usamos .text() ou descartamos a resposta).
  remove: async (id: string, idempotencyKey?: string): Promise<void> => {
    await api.delete(`expenses/${id}`, { headers: idempotencyHeaders(idempotencyKey) });
  },

  removeSeries: async (id: string, idempotencyKey?: string): Promise<void> => {
    await api.delete(`expenses/${id}/series`, { headers: idempotencyHeaders(idempotencyKey) });
  },

  rebalanceSplits: (
    id: string,
    body?: { splits?: Array<{ user_id: string; percentage: number }>; include_today?: boolean },
    idempotencyKey?: string,
  ) =>
    api
      .post(`expenses/${id}/series/rebalance-splits`, {
        json: body ?? {},
        headers: idempotencyHeaders(idempotencyKey),
      })
      .json<ApiResponse<{ affected_expenses: number; from_date: string }>>(),
};

export interface IncomesListParams extends TransactionsListParams {
  source_id?: string;
  is_recurring?: boolean;
}

export const incomesApi = {
  list: (params: IncomesListParams = {}) =>
    api.get('incomes', { searchParams: cleanParams(params) }).json<PaginatedResponse<Income>>(),

  get: (id: string) => api.get(`incomes/${id}`).json<ApiResponse<Income>>(),

  create: (body: CreateIncomePayload, idempotencyKey?: string) =>
    api
      .post('incomes', { json: body, headers: idempotencyHeaders(idempotencyKey) })
      .json<ApiResponse<Income | Income[]>>(),

  update: (id: string, body: UpdateIncomePayload, idempotencyKey?: string) =>
    api
      .patch(`incomes/${id}`, { json: body, headers: idempotencyHeaders(idempotencyKey) })
      .json<ApiResponse<Income>>(),

  remove: async (id: string, idempotencyKey?: string): Promise<void> => {
    await api.delete(`incomes/${id}`, { headers: idempotencyHeaders(idempotencyKey) });
  },

  removeSeries: async (id: string, idempotencyKey?: string): Promise<void> => {
    await api.delete(`incomes/${id}/series`, { headers: idempotencyHeaders(idempotencyKey) });
  },
};
